<template>
  <CollapsibleCard
    ref="spotlightCard"
    :default-expanded="false">
    <template #title>
      <i class="fas fa-broadcast-tower text-purple-600 mr-2" />
      Spotlight Tracking
      <span class="ml-2 text-sm text-gray-500">
        ({{ spotlightPages.length }} pages)
      </span>
    </template>

    <!-- Add Page Button (inside content, so only visible when expanded) -->
    <div class="flex justify-end mb-4">
      <button
        class="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        @click.stop="showAddPageModal = true">
        <i class="fas fa-plus mr-1" />
        Add Page
      </button>
    </div>

    <!-- Stats Cards -->
    <div
      v-if="spotlightPages.length > 0"
      class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-blue-600 text-xs font-medium uppercase tracking-wider">
              Total Pages
            </p>
            <p class="text-2xl font-bold mt-1 text-blue-900">
              {{ stats.total }}
            </p>
          </div>
          <div class="bg-blue-100 rounded-full p-2.5">
            <i class="fas fa-globe text-sm text-blue-600" />
          </div>
        </div>
      </div>

      <div class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-green-600 text-xs font-medium uppercase tracking-wider">
              Widget Installed
            </p>
            <p class="text-2xl font-bold mt-1 text-green-900">
              {{ stats.installed }}
            </p>
          </div>
          <div class="bg-green-100 rounded-full p-2.5">
            <i class="fas fa-check-circle text-sm text-green-600" />
          </div>
        </div>
      </div>

      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-red-600 text-xs font-medium uppercase tracking-wider">
              Errors
            </p>
            <p class="text-2xl font-bold mt-1 text-red-900">
              {{ stats.errors }}
            </p>
          </div>
          <div class="bg-red-100 rounded-full p-2.5">
            <i class="fas fa-exclamation-triangle text-sm text-red-600" />
          </div>
        </div>
      </div>
    </div>

    <!-- Time to First Installation -->
    <div
      v-if="stats.days_to_first_install !== null"
      class="mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
      <p class="text-sm text-indigo-700 flex items-center">
        <i class="fas fa-clock mr-2" />
        First widget installed <span class="font-semibold mx-1">{{ stats.days_to_first_install }}</span> days after customer creation
      </p>
    </div>

    <!-- Installation Responsibility Section -->
    <div class="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-semibold text-gray-700 flex items-center">
          <i class="fas fa-tools mr-2 text-gray-500" />
          Installation Responsibility
        </h4>
        <button
          v-if="!editingResponsibility"
          class="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          @click="startEditingResponsibility">
          <i class="fas fa-edit mr-1" />
          Edit
        </button>
      </div>

      <div v-if="!editingResponsibility">
        <!-- Display Mode -->
        <div class="flex items-center space-x-4">
          <span class="text-sm text-gray-600">Handled by:</span>
          <span
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
            :class="installationResponsibility === 'our_team'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-amber-100 text-amber-800'">
            <i
              class="mr-1.5"
              :class="installationResponsibility === 'our_team'
                ? 'fas fa-users'
                : 'fas fa-user-tie'" />
            {{ installationResponsibility === 'our_team' ? 'Our Team' : 'Client' }}
          </span>
          <div
            v-if="installationResponsibility === 'client' && installationContactEmail"
            class="flex items-center text-sm text-gray-600">
            <i class="fas fa-envelope mr-1.5 text-gray-400" />
            <a
              :href="'mailto:' + installationContactEmail"
              class="text-indigo-600 hover:text-indigo-700">
              {{ installationContactEmail }}
            </a>
          </div>
          <div
            v-else-if="installationResponsibility === 'client' && !installationContactEmail"
            class="text-sm text-red-600 italic">
            <i class="fas fa-exclamation-triangle mr-1" />
            No contact email provided
          </div>
        </div>
      </div>

      <div v-else>
        <!-- Edit Mode -->
        <div class="space-y-3">
          <div class="flex items-center space-x-4">
            <select
              v-model="editedResponsibility"
              class="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm">
              <option value="our_team">
                Our Team
              </option>
              <option value="client">
                Client
              </option>
            </select>

            <input
              v-if="editedResponsibility === 'client'"
              v-model="editedContactEmail"
              type="email"
              placeholder="client@example.com"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              :class="{'border-red-300': editedResponsibility === 'client' && !editedContactEmail}">

            <div class="flex space-x-2">
              <button
                :disabled="savingResponsibility || (editedResponsibility === 'client' && !editedContactEmail)"
                class="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                @click="saveResponsibility">
                <i
                  v-if="savingResponsibility"
                  class="fas fa-spinner fa-spin mr-1" />
                <i
                  v-else
                  class="fas fa-save mr-1" />
                Save
              </button>
              <button
                class="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                @click="cancelEditingResponsibility">
                Cancel
              </button>
            </div>
          </div>

          <p
            v-if="editedResponsibility === 'client' && !editedContactEmail"
            class="text-xs text-red-600">
            <i class="fas fa-exclamation-circle mr-1" />
            Contact email is required when client handles installation
          </p>
        </div>
      </div>
    </div>

    <!-- Page Cards Grid -->
    <div
      v-if="spotlightPages.length > 0"
      class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SpotlightPageCard
        v-for="page in spotlightPages"
        :key="page.id"
        :page="page"
        @check-now="handleCheckNow"
        @edit="handleEdit"
        @delete="handleDelete" />
    </div>

    <!-- Empty State -->
    <div
      v-if="spotlightPages.length === 0"
      class="text-center py-8">
      <i class="fas fa-broadcast-tower text-4xl text-gray-300 mb-3" />
      <p class="text-gray-500">
        No spotlight pages tracked yet
      </p>
      <button
        class="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        @click.stop="showAddPageModal = true">
        <i class="fas fa-plus mr-2" />
        Add First Page
      </button>
    </div>

    <!-- Add Page Modal -->
    <div
      v-if="showAddPageModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">
          Add Spotlight Page
        </h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              URL <span class="text-red-500">*</span>
            </label>
            <input
              v-model="newPage.url"
              type="url"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="https://example.com"
              required>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Page Type
            </label>
            <select
              v-model="newPage.page_type"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500">
              <option value="">
                Select type...
              </option>
              <option value="home">
                Home
              </option>
              <option value="location">
                Location
              </option>
              <option value="service">
                Service
              </option>
              <option value="location-service">
                Location Service
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Geo
            </label>
            <input
              v-model="newPage.geo"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., New York, NY">
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              v-model="newPage.category"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Plumbing Services">
          </div>
        </div>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            @click="cancelAddPage">
            Cancel
          </button>
          <button
            :disabled="!newPage.url || addingPage"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="addPage">
            <span v-if="addingPage">
              <i class="fas fa-spinner fa-spin mr-2" />
              Adding...
            </span>
            <span v-else>Add Page</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Page Modal -->
    <div
      v-if="showEditPageModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">
          Edit Spotlight Page
        </h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              URL <span class="text-red-500">*</span>
            </label>
            <input
              v-model="editPage.url"
              type="url"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="https://example.com"
              required>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Page Type
            </label>
            <select
              v-model="editPage.page_type"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500">
              <option value="">
                Select type...
              </option>
              <option value="home">
                Home
              </option>
              <option value="location">
                Location
              </option>
              <option value="service">
                Service
              </option>
              <option value="location-service">
                Location Service
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Geo
            </label>
            <input
              v-model="editPage.geo"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., New York, NY">
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              v-model="editPage.category"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Plumbing Services">
          </div>
        </div>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            @click="cancelEditPage">
            Cancel
          </button>
          <button
            :disabled="!editPage.url || editingPage"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="updatePage">
            <span v-if="editingPage">
              <i class="fas fa-spinner fa-spin mr-2" />
              Updating...
            </span>
            <span v-else>Update Page</span>
          </button>
        </div>
      </div>
    </div>
  </CollapsibleCard>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { createConsumer } from '@rails/actioncable'
import CollapsibleCard from './collapsible_card.vue'
import SpotlightPageCard from './spotlight_page_card.vue'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// Data
const spotlightPages = ref([])
const stats = ref({
  total: 0,
  installed: 0,
  errors: 0,
  days_to_first_install: null
})
const showAddPageModal = ref(false)
const showEditPageModal = ref(false)
const addingPage = ref(false)
const editingPage = ref(false)

// Installation Responsibility
const installationResponsibility = ref('our_team')
const installationContactEmail = ref('')
const editingResponsibility = ref(false)
const editedResponsibility = ref('our_team')
const editedContactEmail = ref('')
const savingResponsibility = ref(false)
const newPage = ref({
  url: '',
  page_type: '',
  geo: '',
  category: ''
})
const editPage = ref({
  id: null,
  url: '',
  page_type: '',
  geo: '',
  category: ''
})

// ActionCable
let subscription = null
const consumer = createConsumer()

// Methods
const fetchSpotlightPages = async() => {
  try {
    const response = await fetch(`/ajax/organizations/${props.organizationId}/spotlight_pages`)
    const data = await response.json()
    spotlightPages.value = data.pages || []
    stats.value = data.stats || {
      total: 0, installed: 0, errors: 0, days_to_first_install: null
    }
    // Set installation responsibility data
    installationResponsibility.value = data.installation_responsibility || 'our_team'
    installationContactEmail.value = data.installation_contact_email || ''
  } catch (error) {
    // Error fetching spotlight pages
  }
}

// Installation Responsibility Methods
const startEditingResponsibility = () => {
  editedResponsibility.value = installationResponsibility.value
  editedContactEmail.value = installationContactEmail.value
  editingResponsibility.value = true
}

const cancelEditingResponsibility = () => {
  editingResponsibility.value = false
  editedResponsibility.value = installationResponsibility.value
  editedContactEmail.value = installationContactEmail.value
}

const saveResponsibility = async() => {
  if (editedResponsibility.value === 'client' && !editedContactEmail.value) {
    return
  }

  savingResponsibility.value = true

  try {
    const response = await fetch(`/ajax/organizations/${props.organizationId}/update_installation_responsibility`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        installation_responsibility: editedResponsibility.value,
        installation_contact_email: editedResponsibility.value === 'client' ? editedContactEmail.value : ''
      })
    })

    const data = await response.json()

    if (data.success) {
      installationResponsibility.value = editedResponsibility.value
      installationContactEmail.value = editedResponsibility.value === 'client' ? editedContactEmail.value : ''
      editingResponsibility.value = false
    } else {
      // eslint-disable-next-line no-alert
      alert('Error updating installation responsibility. Please try again.')
    }
  } catch (error) {
    // eslint-disable-next-line no-alert
    alert('Error updating installation responsibility. Please try again.')
  } finally {
    savingResponsibility.value = false
  }
}

const addPage = async() => {
  if (!newPage.value.url || addingPage.value) return

  addingPage.value = true

  try {
    const response = await fetch(`/ajax/organizations/${props.organizationId}/spotlight_pages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ spotlight_page: newPage.value })
    })

    const data = await response.json()

    if (data.success) {
      // Add the new page to the list
      spotlightPages.value.unshift(data.page)
      stats.value.total += 1

      // Reset form and close modal
      newPage.value = {
        url: '', page_type: '', geo: '', category: ''
      }
      showAddPageModal.value = false

      // Show success message (you might want to use a toast library)
      // Page added successfully
    } else {
      // Error adding page
      // eslint-disable-next-line no-alert
      alert(data.errors?.join(', ') || 'Error adding page')
    }
  } catch (error) {
    // Error adding page
    // eslint-disable-next-line no-alert
    alert('Error adding page. Please try again.')
  } finally {
    addingPage.value = false
  }
}

const cancelAddPage = () => {
  newPage.value = {
    url: '', page_type: '', geo: '', category: ''
  }
  showAddPageModal.value = false
}

const handleCheckNow = async(pageId) => {
  try {
    const response = await fetch(`/ajax/spotlight_pages/${pageId}/check_now`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })

    const data = await response.json()

    if (data.success) {
      // Update the page status to checking
      const page = spotlightPages.value.find((p) => p.id === pageId)
      if (page) {
        page.last_check_status = 'checking'
      }
    }
  } catch (error) {
    // Error triggering check
  }
}

const handleEdit = (page) => {
  editPage.value = {
    id: page.id,
    url: page.url,
    page_type: page.page_type || '',
    geo: page.geo || '',
    category: page.category || ''
  }
  showEditPageModal.value = true
}

const updatePage = async() => {
  if (!editPage.value.url || editingPage.value) return

  editingPage.value = true

  try {
    const response = await fetch(`/ajax/organizations/${props.organizationId}/spotlight_pages/${editPage.value.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ spotlight_page: editPage.value })
    })

    const data = await response.json()

    if (data.success) {
      // Update the page in the list
      const index = spotlightPages.value.findIndex((p) => p.id === editPage.value.id)
      if (index > -1) {
        spotlightPages.value[index] = { ...spotlightPages.value[index], ...data.page }
      }

      // Reset form and close modal
      editPage.value = {
        id: null, url: '', page_type: '', geo: '', category: ''
      }
      showEditPageModal.value = false
    } else {
      // eslint-disable-next-line no-alert
      alert(data.errors?.join(', ') || 'Error updating page')
    }
  } catch (error) {
    // Error updating page
    // eslint-disable-next-line no-alert
    alert('Error updating page. Please try again.')
  } finally {
    editingPage.value = false
  }
}

const cancelEditPage = () => {
  editPage.value = {
    id: null, url: '', page_type: '', geo: '', category: ''
  }
  showEditPageModal.value = false
}

const handleDelete = async(pageId) => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm('Are you sure you want to delete this spotlight page?')) return

  try {
    const response = await fetch(`/ajax/organizations/${props.organizationId}/spotlight_pages/${pageId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })

    const data = await response.json()

    if (data.success) {
      // Remove from list
      const index = spotlightPages.value.findIndex((p) => p.id === pageId)
      if (index > -1) {
        const page = spotlightPages.value[index]
        spotlightPages.value.splice(index, 1)

        // Update stats
        stats.value.total -= 1
        if (page.widget_installed) stats.value.installed -= 1
        if (page.last_check_status === 'error') stats.value.errors -= 1
      }
    } else {
      // eslint-disable-next-line no-alert
      alert('Error deleting page. Please try again.')
    }
  } catch (error) {
    // Error deleting page
    // eslint-disable-next-line no-alert
    alert('Error deleting page. Please try again.')
  }
}

// ActionCable subscription for real-time updates
const setupSubscription = () => {
  subscription = consumer.subscriptions.create(
    {
      channel: 'OrganizationChannel',
      organization_id: props.organizationId
    },
    {
      connected() {
        // Connected to OrganizationChannel
      },
      disconnected() {
        // Disconnected from OrganizationChannel
      },
      received(data) {
        // Received data from ActionCable
        if (data.event === 'spotlight_updated') {
          // Update the specific page
          const page = spotlightPages.value.find((p) => p.id === data.page_id)
          if (page) {
            // Updating page
            page.last_check_status = data.status
            page.widget_installed = data.widget_installed
            page.last_check_at = data.last_check_at
            page.last_check_error = data.last_check_error
            page.installation_days = data.installation_days

            // Recalculate stats
            stats.value.installed = spotlightPages.value.filter((p) => p.widget_installed).length
            stats.value.errors = spotlightPages.value.filter((p) => p.last_check_status === 'error').length
          }
        }
      }
    }
  )
}

// Lifecycle
onMounted(() => {
  fetchSpotlightPages()
  setupSubscription()
})

onUnmounted(() => {
  if (subscription) {
    subscription.unsubscribe()
  }
})
</script>
